import {
  Autosuggest,
  Button,
  Cell,
  Checkbox,
  DialogOverlay,
  DialogOverlayActions,
  FilterChips,
  Grid,
  InputGroup,
  Row,
  Radio,
  RadioGroup,
  Select,
  TextField,
  useChipCollection,
  H5,
} from '@fs/zion-ui'
import React, { useEffect } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'
import { constants } from './useCreateResultQuery'

export default () => {}

const pill = css`
  background-color: rgb(187, 209, 31);
  border: none;
  color: black;
  padding: 10px 20px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  margin: 4px 2px;
  border-radius: 16px;
`

const center = css`
  text-align: center;
`

export function CreateResultQuery({ createResultQueryState, createResultQueryActions }) {
  const { t } = useTranslation()
  return (
    <DialogOverlay size="xl" {...createResultQueryActions.dialogOverlay} closeOnClickAway={false}>
      <Grid>
        {/* Row */}
        <Cell columns={12}>
          <div css={center}>
            <H5>{t('dats.createResultQuery', 'Create Result Query')}</H5>
          </div>
        </Cell>

        {/* Row */}
        <Cell columns={6}>
          <InputGroup label={t('dats.selectColumns', 'Select Columns')}>
            <Select
              value={createResultQueryState.formSelections.columnFilterType}
              options={createResultQueryState.formOptions.columnFilterTypes}
              onChange={(event) => createResultQueryActions.setColumnFilterType(event.target.value)}
            />
          </InputGroup>
        </Cell>
        <p columns={6} />

        {/* Row */}
        {createResultQueryState.formSelections.columnFilterType !== constants.ALL_COLUMNS && (
          <Cell columns={12}>
            <FilterColumns
              createResultQueryState={createResultQueryState}
              createResultQueryActions={createResultQueryActions}
            />
          </Cell>
        )}

        {/* Row */}
        <Cell columns={12}>
          <InputGroup label={t('dats.selectItems', 'Select Items')}>
            <FailedOrSuccessfulItems
              createResultQueryState={createResultQueryState}
              createResultQueryActions={createResultQueryActions}
            />
          </InputGroup>
        </Cell>

        {/* Row */}
        <Cell columns={12}>
          <InputGroup label={t('dats.filterItems', 'Filter Items')}>
            <FilterRows
              createResultQueryState={createResultQueryState}
              createResultQueryActions={createResultQueryActions}
            />
          </InputGroup>
        </Cell>

        {/* Row */}
        <Cell columns={12}>
          <FilterRowNew
            createResultQueryState={createResultQueryState}
            createResultQueryActions={createResultQueryActions}
          />
        </Cell>

        {/* Row */}
        <Cell columns={12}>
          <InputGroup>
            <Checkbox
              label={t('dats.createZipFile', 'Create Zip File')}
              checked={createResultQueryState.formSelections.createFile}
              onChange={() => createResultQueryActions.toggleCreateFile()}
            />
          </InputGroup>
        </Cell>
      </Grid>

      <DialogOverlayActions>
        <InputGroup>
          <Row>
            <Button emphasis="high" onClick={() => createResultQueryActions.makeResultQuery()}>
              {t('action.submit', 'Submit')}
            </Button>
            <Button data-testid="closeButton" emphasis="medium" onClick={() => createResultQueryActions.close()}>
              {t('action.cancel', 'Cancel')}
            </Button>
          </Row>
        </InputGroup>
      </DialogOverlayActions>
    </DialogOverlay>
  )
}

function FilterColumns({ createResultQueryState, createResultQueryActions }) {
  const { t } = useTranslation()

  // Built in columns
  const builtinColumnChipsCollection = useChipCollection(createResultQueryState.formOptions.builtinColumnChips)
  const builtinChipLastTouched = builtinColumnChipsCollection.lastTouched

  useEffect(() => {
    if (builtinChipLastTouched) {
      if (builtinChipLastTouched.selected) {
        createResultQueryActions.addJustIncludeColumn(builtinChipLastTouched)
      } else {
        createResultQueryActions.delJustIncludeColumn(builtinChipLastTouched)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builtinChipLastTouched])

  // Input Columns
  const inputColumnChipsCollection = useChipCollection(createResultQueryState.formOptions.inputColumnChips)
  const inputChipLastTouched = inputColumnChipsCollection.lastTouched

  useEffect(() => {
    if (inputChipLastTouched) {
      if (inputChipLastTouched.selected) {
        createResultQueryActions.addJustIncludeColumn(inputChipLastTouched)
      } else {
        createResultQueryActions.delJustIncludeColumn(inputChipLastTouched)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputChipLastTouched])

  // Output Columns
  const outputColumnChipsCollection = useChipCollection(createResultQueryState.formOptions.outputColumnChips)
  const outputChipLastTouched = outputColumnChipsCollection.lastTouched

  useEffect(() => {
    if (outputChipLastTouched) {
      if (outputChipLastTouched.selected) {
        createResultQueryActions.addJustIncludeColumn(outputChipLastTouched)
      } else {
        createResultQueryActions.delJustIncludeColumn(outputChipLastTouched)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputChipLastTouched])

  return (
    <>
      {createResultQueryState.formOptions.builtinColumnChips.length > 0 && (
        <InputGroup label={t('dats.builtInColumns', 'Built in Columns')}>
          <FilterChips chipCollection={builtinColumnChipsCollection} />
        </InputGroup>
      )}

      {createResultQueryState.formOptions.inputColumnChips.length > 0 && (
        <InputGroup label={t('dats.inputColumns', 'Input Columns')}>
          <FilterChips chipCollection={inputColumnChipsCollection} />
        </InputGroup>
      )}

      {createResultQueryState.formOptions.outputColumnChips.length > 0 && (
        <InputGroup label={t('dats.outputColumns', 'Output Columns')}>
          <FilterChips chipCollection={outputColumnChipsCollection} />
        </InputGroup>
      )}

      {createResultQueryState.formSelections.columnFilters.length > 0 && (
        <InputGroup label={t('dats.orderOfSelectedColumns', 'Order of Selected Columns')}>
          {createResultQueryState.formSelections.columnFilters.map((column) => {
            return (
              <span data-testid="orderedPill">
                <span css={pill} key={column.key}>
                  {t(`dats.parameterName.${column.columnName}`, `${column.columnName}`)}
                </span>
                &nbsp;&nbsp;
              </span>
            )
          })}
        </InputGroup>
      )}
    </>
  )
}

function FailedOrSuccessfulItems({ createResultQueryState, createResultQueryActions }) {
  return (
    <RadioGroup>
      {createResultQueryState.formOptions.itemsFilterRadioButons.map((radioButton) => {
        return (
          <Radio
            name="failedOrSuccessfulItems"
            key={v4()}
            checked={createResultQueryState.formSelections.itemsRadioButton === radioButton.value}
            label={radioButton.label}
            value={radioButton.value}
            onChange={(event) => createResultQueryActions.setItemsRadioButton(event.target.value)}
          />
        )
      })}
    </RadioGroup>
  )
}

function FilterRows({ createResultQueryState, createResultQueryActions }) {
  return (
    <>
      {Object.keys(createResultQueryState.formSelections.rowFilters).map((rowFilterKey) => {
        const rowFilter = createResultQueryState.formSelections.rowFilters[rowFilterKey]
        return (
          <FilterRow
            key={rowFilter.rowId}
            column={rowFilter.columnName}
            operation={rowFilter.operation}
            value={rowFilter.value}
            rowId={rowFilter.rowId}
            createResultQueryState={createResultQueryState}
            createResultQueryActions={createResultQueryActions}
          />
        )
      })}
    </>
  )
}

function FilterRow({ column, operation, value, rowId, createResultQueryActions, createResultQueryState }) {
  return (
    <Grid data-testid={`filterRow_${rowId}`}>
      <Cell columns={3}>
        <Autosuggest selectedItem={{ primaryText: column }} disabled />
      </Cell>
      <Cell columns={3}>
        <Select value={operation} options={createResultQueryState.formOptions.rowFilterOperations} disabled />
      </Cell>
      <Cell columns={5}>
        <TextField value={value} disabled />
      </Cell>
      <Cell columns={1}>
        <Button emphasis="high" onClick={() => createResultQueryActions.deleteRowFilter(rowId)}>
          -
        </Button>
      </Cell>
    </Grid>
  )
}

function FilterRowNew({ createResultQueryState, createResultQueryActions }) {
  return (
    <Grid>
      <Cell columns={3}>
        <Autosuggest
          label="Column"
          selectedItem={createResultQueryState.formSelections.rowFilterSelectColumn}
          suggestions={createResultQueryState.formOptions.rowFilterColumnSuggestions}
          onSuggestionSelected={(newSuggestion) =>
            newSuggestion && newSuggestion.group && createResultQueryActions.setRowFilterSelectColumn(newSuggestion)
          }
          onClear={() => createResultQueryActions.setRowFilterSelectColumn(undefined)}
          placeholder="None"
          highlightMatchingText
          clearable
        />
      </Cell>

      <Cell columns={3}>
        <Select
          label="Option"
          data-testid="rowFilterOperations"
          value={createResultQueryState.formSelections.rowFilterOperation}
          options={createResultQueryState.formOptions.rowFilterOperations}
          onChange={(event) => createResultQueryActions.setRowFilterOperation(event.target.value)}
        />
      </Cell>

      <Cell columns={5}>
        <TextField
          label="Query Term"
          value={createResultQueryState.formSelections.rowFilterText}
          onChange={(event) => createResultQueryActions.setRowFilterText(event.target.value)}
        />
      </Cell>

      <Cell columns={1}>
        <Button
          emphasis="high"
          onClick={() => createResultQueryActions.addRowFilter()}
          disabled={
            !(
              createResultQueryState.formSelections.rowFilterSelectColumn &&
              createResultQueryState.formSelections.rowFilterOperation &&
              createResultQueryState.formSelections.rowFilterText
            )
          }
        >
          +
        </Button>
      </Cell>
    </Grid>
  )
}
